import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../asset/images/home/Logo (1).png";
import FastTrackLogo from "../../asset/images/home/Fast Track logo 1.png";

function VerifyEmail() {
    const [token, setToken] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const { verifyEmail, updateUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const tokenFromUrl = searchParams.get("token");
        if (tokenFromUrl) {
            setToken(tokenFromUrl);
            handleVerify(tokenFromUrl);
        }
    }, []);

    const handleVerify = async (verifyToken) => {
        if (!verifyToken) {
            setError("Verification token is required");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const result = await verifyEmail(verifyToken);

            if (result.success) {
                
                const { user, token, refreshToken } = result.data;
                
                if (token && refreshToken && user) {
                    localStorage.setItem('token', token);
                    localStorage.setItem('refreshToken', refreshToken);
                    localStorage.setItem('user', JSON.stringify(user));
                    
                    updateUser(user);
                }
                
                setSuccess(true);
                setTimeout(() => {
                    navigate("/register/payment-card");
                }, 2000);
            } else {
                setError(result.error || "Email verification failed");
            }
        } catch (err) {
            setError("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleVerify(token);
    };

    return (
        <>
            {/* Header */}
            <header className="bg-white shadow-md">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex justify-between items-center">
                        {/* Logo */}
                        <div className="flex items-center">
                            <img src={Logo} alt="READ 21 Logo" className="h-16 w-auto" />
                        </div>

                        {/* Navigation */}
                        <div className="flex items-center space-x-8">
                            <Link
                                to="/about"
                                className="bg-transparent text-gray-700 px-8 py-3 rounded-md font-medium text-lg hover:bg-primaryDark hover:text-white transition-colors"
                            >
                                About
                            </Link>

                            <Link to="/register">
                                <button className="bg-primary text-white px-8 py-3 rounded-md font-medium hover:bg-primaryDark transition-colors text-lg">
                                    Enroll Today
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Section */}
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="bg-white rounded-xl shadow-xl p-10">
                        {/* Grid Layout */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side */}
                            <div className="flex justify-center">
                                <img src={FastTrackLogo} alt="READ 21 Logo" className="w-64 h-auto" />
                            </div>

                            {/* Right Side */}
                            <div className="space-y-5">
                                <div className="mb-12">
                                    <h1 className="text-3xl md:text-4xl font-semibold text-primary text-center mb-6">
                                        Verify Your Email Address
                                    </h1>
                                    <div className="border-b-2 mb-6"></div>
                                    {!success && (
                                        <p className="text-center text-sm">
                                            Please enter the verification token sent to your email, or click the link in the email.
                                        </p>
                                    )}
                                </div>

                                {/* Success Message */}
                                {success && (
                                    <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                                        <p className="text-sm text-green-600 text-center">
                                            Email verified successfully! Redirecting to payment...
                                        </p>
                                    </div>
                                )}

                                {/* Error Message */}
                                {error && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                                        <p className="text-sm text-red-600">{error}</p>
                                    </div>
                                )}

                                {!success && (
                                    <form onSubmit={handleSubmit}>
                                        {/* Token Input */}
                                        <div className="relative mb-4">
                                            <input
                                                type="text"
                                                placeholder="Enter verification token"
                                                value={token}
                                                onChange={(e) => setToken(e.target.value)}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                            />
                                        </div>

                                        {/* Verify Button */}
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full bg-primary hover:bg-primaryDark text-white font-semibold py-4 rounded-md transition-colors text-lg mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading ? "Verifying..." : "Verify Email"}
                                        </button>
                                    </form>
                                )}

                                {/* Resend Link */}
                                {!success && (
                                    <p className="text-center text-sm text-gray-600 mt-4">
                                        Didn't receive the email?{" "}
                                        <Link to="/register" className="text-primary hover:underline font-medium">
                                            Register again
                                        </Link>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-primary py-10">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <div className="mb-3">
                        <img src={Logo} alt="READ 21 Logo" className="mx-auto h-16 w-auto" />
                    </div>
                    <p className="text-white text-lg">Read 21 © 2025 - All Rights Reserved</p>
                </div>
            </footer>
        </>
    );
}

export default VerifyEmail;
